/**
 * Inspections — the human-driven half of the operational loop.
 *
 * Shapes follow the platform's `inspection`, `observation` and
 * `checklist_template` tables as `work.py` serves them (`list_inspections`,
 * `get_inspection`). Starting a round and recording answers belongs to the
 * mobile client; this web module reads and reviews: which rounds ran, what
 * failed, and what each failure produced.
 */

export const INSPECTION_STATUSES = ["scheduled", "in_progress", "completed", "cancelled"] as const;
export type InspectionStatus = (typeof INSPECTION_STATUSES)[number];

export const INSPECTION_STATUS_LABEL: Record<InspectionStatus, string> = {
  scheduled: "Scheduled",
  in_progress: "In progress",
  completed: "Completed",
  cancelled: "Canceled",
};

/** `observation.result`: pass | fail | na | value (a measurement, judged by the pack). */
export type ObservationResult = "pass" | "fail" | "na" | "value";

export interface Inspection {
  id: string;
  siteId: string;
  status: InspectionStatus;
  /** Checklist template key and name, e.g. airfield_daily_quick. */
  template: string;
  templateName: string;
  depth?: "quick" | "detailed";
  /** Pack-defined: airfield | terminal | landside … */
  domain?: string;
  zoneCode?: string;
  scheduledFor?: string;
  startedAt?: string;
  completedAt?: string;
  /** 0–100 as the pack's scoring computed it, when completed. */
  score?: number;
  /** Share of the checklist answered, 0–100. */
  coveragePct?: number;
  capturedOffline?: boolean;
  answers: number;
  failures: number;
  /** Answers skipped — not looked at, with a reason. Neither a pass nor a failure. */
  skipped?: number;
}

export interface Observation {
  id: string;
  itemKey: string;
  result?: ObservationResult;
  valueNum?: number;
  valueText?: string;
  notes?: string;
  skippedReason?: string;
  assetTag?: string;
  zoneCode?: string;
  /** The defect this failure raised, when it did. */
  defectId?: string;
  point?: { lon: number; lat: number };
  photos: number;
}

export interface InspectionDetail extends Inspection {
  attrs?: Record<string, unknown>;
  /** The route driven, as the platform recorded it. */
  route?: unknown;
  scoring?: Record<string, unknown>;
  observations: Observation[];
}

export function isFailure(o: Observation): boolean {
  return o.result === "fail";
}

/** When the round happened, for sorting and display: completion, else start, else schedule. */
export function inspectionWhen(i: Inspection): string | undefined {
  return i.completedAt ?? i.startedAt ?? i.scheduledFor;
}

export function sortInspections(rows: readonly Inspection[]): Inspection[] {
  return [...rows].sort((a, b) => (inspectionWhen(b) ?? "").localeCompare(inspectionWhen(a) ?? ""));
}

export interface InspectionSummary {
  total: number;
  completed: number;
  withFailures: number;
  failures: number;
  /** Mean score over completed rounds that have one, or null. */
  meanScore: number | null;
}

export function summariseInspections(rows: readonly Inspection[]): InspectionSummary {
  const completed = rows.filter((r) => r.status === "completed");
  const scored = completed.filter((r) => r.score != null);
  return {
    total: rows.length,
    completed: completed.length,
    withFailures: rows.filter((r) => r.failures > 0).length,
    failures: rows.reduce((t, r) => t + r.failures, 0),
    meanScore: scored.length ? scored.reduce((t, r) => t + (r.score as number), 0) / scored.length : null,
  };
}

/** Human label for a checklist item key: "fod_present" → "Fod present". */
export function itemLabel(key: string): string {
  const s = key.replaceAll("_", " ");
  return s.charAt(0).toUpperCase() + s.slice(1);
}
